import { z } from 'zod';
import { patientCategorySchema } from './blood';
import { DEFAULT_PAGE_SIZE, MAX_PAGE_SIZE } from './organization';
/**
 * Money travels over the wire as a STRING, not a number.
 *
 * Rupiah is stored as bigint in the database, and JSON.parse would turn large values
 * into double-precision floats. A string keeps the value intact from the database all
 * the way to the screen.
 */
export const moneySchema = z.string().regex(/^\d{1,15}$/, 'Nominal harus angka bulat non-negatif');
export const priceInputSchema = z.object({
    componentTypeId: z.string().uuid(),
    patientCategory: patientCategorySchema,
    /** null = national tariff. Set = an override for that branch. */
    branchId: z.string().uuid().optional().nullable(),
    basePrice: moneySchema,
    discountAmount: moneySchema.default('0'),
    subsidyAmount: moneySchema.default('0'),
    /** When this tariff takes effect. Always explicit — never silently "now". */
    effectiveFrom: z.string().datetime({ offset: true }),
    note: z.string().trim().max(500).optional().nullable(),
}).superRefine((v, ctx) => {
    const base = BigInt(v.basePrice);
    const cut = BigInt(v.discountAmount) + BigInt(v.subsidyAmount);
    if (cut > base) {
        ctx.addIssue({
            code: 'custom', path: ['discountAmount'],
            message: 'Diskon ditambah subsidi tidak boleh melebihi harga dasar.',
        });
    }
});
export const priceQuerySchema = z.object({
    componentTypeId: z.string().uuid(),
    patientCategory: patientCategorySchema,
    branchId: z.string().uuid().optional(),
    /** Defaults to now. Set it to see the tariff in force on a past date. */
    at: z.string().datetime({ offset: true }).optional(),
});
/**
 * Tariff history for one price key, paged.
 *
 * The key itself (component, category, branch) is a required filter rather than a search
 * term: a mixed history is meaningless, because two rows from different keys can both be
 * "in force" without overlapping. `q` searches the note, which is where the legal basis
 * for a tariff lives.
 */
export const priceHistoryQuerySchema = z.object({
    componentTypeId: z.string().uuid(),
    patientCategory: patientCategorySchema,
    branchId: z.string().uuid().optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(DEFAULT_PAGE_SIZE),
    q: z.string().trim().max(200).optional(),
});
/** Format rupiah for display. Takes a string so bigint precision survives. */
export function formatRupiah(amount) {
    const n = amount.replace(/\D/g, '') || '0';
    return `Rp ${n.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`;
}
